import { useState, useEffect, useCallback } from "react";
import { Search, TrendingUp, TrendingDown, Star, X, RefreshCw, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import StockChart from "@/components/StockChart";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// API functions
const searchStocks = async (keywords) => {
  const { data } = await axios.get(`${API}/stocks/search?keywords=${encodeURIComponent(keywords)}`);
  return data;
};

const getStockQuote = async (symbol) => {
  const { data } = await axios.get(`${API}/stocks/${symbol}/quote`);
  return data;
};

const getIntradayData = async (symbol) => {
  const { data } = await axios.get(`${API}/stocks/${symbol}/intraday?interval=5min`);
  return data;
};

const getWatchlist = async () => {
  const { data } = await axios.get(`${API}/watchlist`);
  return data;
};

const addToWatchlist = async ({ symbol, name }) => {
  const { data } = await axios.post(`${API}/watchlist`, { symbol, name });
  return data;
};

const removeFromWatchlist = async (symbol) => {
  const { data } = await axios.delete(`${API}/watchlist/${symbol}`);
  return data;
};

export default function Dashboard() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Watchlist query
  const { data: watchlist = [], isLoading: watchlistLoading } = useQuery({
    queryKey: ["watchlist"],
    queryFn: getWatchlist,
  });

  // Selected stock quote
  const { data: stockQuote, isLoading: quoteLoading, refetch: refetchQuote } = useQuery({
    queryKey: ["quote", selectedStock?.symbol],
    queryFn: () => getStockQuote(selectedStock.symbol),
    enabled: !!selectedStock,
    refetchInterval: 60000, // Refresh every minute
  });

  // Intraday data for chart
  const { data: intradayData = [], isLoading: chartLoading } = useQuery({
    queryKey: ["intraday", selectedStock?.symbol],
    queryFn: () => getIntradayData(selectedStock.symbol),
    enabled: !!selectedStock,
  });

  // Add to watchlist mutation
  const addMutation = useMutation({
    mutationFn: addToWatchlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success("Added to watchlist");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to add to watchlist");
    },
  });

  // Remove from watchlist mutation
  const removeMutation = useMutation({
    mutationFn: removeFromWatchlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success("Removed from watchlist");
    },
    onError: () => {
      toast.error("Failed to remove from watchlist");
    },
  });

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchStocks(searchQuery);
        setSearchResults(results);
      } catch (error) {
        if (error.response?.status === 429) {
          toast.error("API rate limit reached. Please wait a moment.");
        }
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectStock = useCallback((stock) => {
    setSelectedStock(stock);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  const isInWatchlist = useCallback(
    (symbol) => watchlist.some((item) => item.symbol === symbol),
    [watchlist]
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
    return volume.toString();
  };

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8" data-testid="dashboard">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              Market<span className="text-primary">Stream</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="market-open" />
            <span className="text-xs text-muted-foreground">LIVE</span>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="stock-search-input"
            type="text"
            placeholder="Search stocks by symbol or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-card border-border search-input"
          />
          {/* Search Results Dropdown */}
          {(searchResults.length > 0 || isSearching) && (
            <Card className="absolute top-full left-0 right-0 mt-2 z-50 glass">
              <ScrollArea className="max-h-[300px]">
                {isSearching ? (
                  <div className="p-4 space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="p-2">
                    {searchResults.map((result) => (
                      <button
                        key={result.symbol}
                        data-testid={`search-result-${result.symbol}`}
                        onClick={() =>
                          handleSelectStock({ symbol: result.symbol, name: result.name })
                        }
                        className="w-full text-left p-3 hover:bg-secondary/50 rounded-sm flex items-center justify-between transition-colors"
                      >
                        <div>
                          <span className="font-mono font-medium text-foreground">
                            {result.symbol}
                          </span>
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {result.name}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {result.type}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Card>
          )}
        </div>
      </header>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Watchlist Sidebar */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <Card className="glass card-hover h-full">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Star className="w-4 h-4 text-accent" />
                Watchlist
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] lg:h-[calc(100vh-280px)]">
                {watchlistLoading ? (
                  <div className="p-4 space-y-2">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : watchlist.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No stocks in watchlist</p>
                    <p className="text-xs mt-1">Search and add stocks to track</p>
                  </div>
                ) : (
                  <div>
                    {watchlist.map((item) => (
                      <WatchlistItem
                        key={item.symbol}
                        item={item}
                        isSelected={selectedStock?.symbol === item.symbol}
                        onSelect={() => handleSelectStock(item)}
                        onRemove={() => removeMutation.mutate(item.symbol)}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-9 order-1 lg:order-2 space-y-4">
          {selectedStock ? (
            <>
              {/* Stock Header */}
              <Card className="glass animate-fade-in-up">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h2
                          data-testid="selected-stock-symbol"
                          className="text-2xl md:text-3xl font-black tracking-tight"
                          data-ticker
                        >
                          {selectedStock.symbol}
                        </h2>
                        <Button
                          data-testid="watchlist-toggle-btn"
                          variant={isInWatchlist(selectedStock.symbol) ? "secondary" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (isInWatchlist(selectedStock.symbol)) {
                              removeMutation.mutate(selectedStock.symbol);
                            } else {
                              addMutation.mutate({
                                symbol: selectedStock.symbol,
                                name: selectedStock.name,
                              });
                            }
                          }}
                          disabled={addMutation.isPending || removeMutation.isPending}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isInWatchlist(selectedStock.symbol)
                                ? "fill-accent text-accent"
                                : ""
                            }`}
                          />
                        </Button>
                      </div>
                      <p className="text-muted-foreground">{selectedStock.name}</p>
                    </div>

                    {quoteLoading ? (
                      <div className="space-y-2">
                        <Skeleton className="h-10 w-32" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    ) : stockQuote ? (
                      <div className="text-right">
                        <p
                          data-testid="stock-price"
                          className="text-3xl md:text-4xl font-mono font-medium"
                          data-price
                        >
                          {formatPrice(stockQuote.price)}
                        </p>
                        <div
                          className={`flex items-center justify-end gap-1 ${
                            stockQuote.change >= 0 ? "text-green-500 glow-green" : "text-red-500 glow-red"
                          }`}
                        >
                          {stockQuote.change >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          <span className="font-mono" data-price>
                            {stockQuote.change >= 0 ? "+" : ""}
                            {stockQuote.change.toFixed(2)} ({stockQuote.change_percent.toFixed(2)}%)
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Stock Stats */}
                  {stockQuote && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-border">
                      <StatItem label="Open" value={formatPrice(stockQuote.open)} />
                      <StatItem label="High" value={formatPrice(stockQuote.high)} />
                      <StatItem label="Low" value={formatPrice(stockQuote.low)} />
                      <StatItem label="Volume" value={formatVolume(stockQuote.volume)} />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Chart */}
              <Card className="glass animate-fade-in-up stagger-1">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Intraday Chart
                    </CardTitle>
                    <Button
                      data-testid="refresh-chart-btn"
                      variant="ghost"
                      size="sm"
                      onClick={() => refetchQuote()}
                      className="h-8"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {chartLoading ? (
                    <Skeleton className="h-[300px] w-full" />
                  ) : (
                    <StockChart
                      data={intradayData}
                      isPositive={stockQuote?.change >= 0}
                    />
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Empty State */
            <Card className="glass">
              <CardContent className="p-8 md:p-12 text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 text-primary opacity-50" />
                <h3 className="text-xl font-bold mb-2">Welcome to MarketStream</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Search for a stock symbol above to view real-time price data, charts, and add
                  stocks to your watchlist.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {["AAPL", "MSFT", "GOOGL", "AMZN", "TSLA"].map((symbol) => (
                    <Button
                      key={symbol}
                      data-testid={`quick-pick-${symbol}`}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectStock({ symbol, name: symbol })}
                      className="font-mono"
                    >
                      {symbol}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

// Stat Item Component
function StatItem({ label, value }) {
  return (
    <div>
      <p className="label-overline mb-1">{label}</p>
      <p className="font-mono text-sm" data-price>
        {value}
      </p>
    </div>
  );
}

// Watchlist Item Component
function WatchlistItem({ item, isSelected, onSelect, onRemove }) {
  const { data: quote } = useQuery({
    queryKey: ["watchlist-quote", item.symbol],
    queryFn: () => getStockQuote(item.symbol),
    refetchInterval: 120000, // Refresh every 2 minutes
    staleTime: 60000,
  });

  return (
    <div
      data-testid={`watchlist-item-${item.symbol}`}
      className={`watchlist-item cursor-pointer group ${isSelected ? "bg-secondary/50" : ""}`}
      onClick={onSelect}
    >
      <div className="flex-1 min-w-0">
        <p className="font-mono font-medium text-sm" data-ticker>
          {item.symbol}
        </p>
        <p className="text-xs text-muted-foreground truncate">{item.name}</p>
      </div>
      <div className="flex items-center gap-2">
        {quote ? (
          <div className="text-right">
            <p className="font-mono text-sm" data-price>
              ${quote.price.toFixed(2)}
            </p>
            <p
              className={`font-mono text-xs ${
                quote.change >= 0 ? "text-green-500" : "text-red-500"
              }`}
              data-price
            >
              {quote.change >= 0 ? "+" : ""}
              {quote.change_percent.toFixed(2)}%
            </p>
          </div>
        ) : (
          <Skeleton className="h-8 w-16" />
        )}
        <Button
          data-testid={`remove-watchlist-${item.symbol}`}
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
